import type { ImageCrop } from '../shared/collections';
import { MAX_COLLECTION_IMAGE_BYTES, MAX_COLLECTION_SOURCE_BYTES } from '../shared/collections';
import { t } from './i18n';

export const DEFAULT_CROP: ImageCrop = { zoom: 1, x: 0, y: 0 };
export async function decodeImage(source: string): Promise<HTMLImageElement> {
  const image = new Image();
  image.src = source;
  try { await image.decode(); }
  catch { throw new Error(t('Impossible de lire cette image. Choisis un autre fichier.')); }
  if (!image.naturalWidth || !image.naturalHeight || image.naturalWidth * image.naturalHeight > 40_000_000)
    throw new Error(t('Cette image est trop grande. Choisis une image de moins de 40 mégapixels.'));
  return image;
}
function boundedImage(canvas: HTMLCanvasElement, maxBytes: number): string {
  for (const quality of [.92, .82, .68, .5]) {
    const image = canvas.toDataURL('image/webp', quality);
    // A failed canvas export can return "data:," instead of throwing.
    if (image.startsWith('data:image/') && Math.ceil((image.length - image.indexOf(',') - 1) * .75) <= maxBytes) return image;
  }
  throw new Error(t('Cette image est trop lourde. Choisis une image plus simple.'));
}
export async function prepareImage(file: File): Promise<string> {
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type))
    throw new Error(t('Choisis une image PNG, JPEG ou WebP.'));
  if (file.size > 12 * 1024 * 1024) throw new Error(t('Chaque fichier image doit peser moins de 12 Mo.'));
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await decodeImage(objectUrl);
    const scale = Math.min(1, 1024 / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    canvas.getContext('2d')!.drawImage(image, 0, 0, canvas.width, canvas.height);
    return boundedImage(canvas, MAX_COLLECTION_SOURCE_BYTES);
  } finally { URL.revokeObjectURL(objectUrl); }
}
export function drawCrop(canvas: HTMLCanvasElement, image: HTMLImageElement, crop: ImageCrop): void {
  const context = canvas.getContext('2d')!;
  const size = canvas.width;
  context.clearRect(0, 0, size, size);
  const ratio = Math.min(size / image.naturalWidth, size / image.naturalHeight) * crop.zoom;
  const width = image.naturalWidth * ratio, height = image.naturalHeight * ratio;
  context.drawImage(image, (size - width) / 2 + crop.x * size / 2, (size - height) / 2 + crop.y * size / 2, width, height);
}
export async function cropImage(source: string, crop: ImageCrop): Promise<string> {
  const image = await decodeImage(source);
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 512;
  drawCrop(canvas, image, crop);
  return boundedImage(canvas, MAX_COLLECTION_IMAGE_BYTES);
}
